import { Box } from "@mui/material";
import classNames from "classnames";
import { PluggableList } from "node_modules/react-markdown/lib";
import * as React from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import type { Components } from "react-markdown";
import rehypeExternalLinks from "rehype-external-links";
import remarkEmoji from "remark-emoji";
import remarkGfm from "remark-gfm";

import { CodeBlock } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/CodeBlock";
import {
  isDatasetLink,
  isFileLink,
  isMessagePathLink,
  isRobotoUri,
  isTimestampLink,
  isTopicLink,
} from "@/shared/components/RobotoMarkdown/magicLinks";
import styles from "@/shared/components/RobotoMarkdown/RobotoMarkdown.module.css";
import type {
  DatasetLinkRenderer,
  FileLinkRenderer,
  MessagePathLinkRenderer,
  TopicLinkRenderer,
} from "@/shared/components/RobotoMarkdown/RobotoMarkdownLink";
import { useMarkdownClipboardCopy } from "@/shared/components/RobotoMarkdown/useMarkdownClipboardCopy";
import { browserSupportsNamedGroups } from "@/shared/text";

import { AgentThreadMarkdownLink } from "./AgentThreadMarkdownLink";
import { isAIInlineTimestampLink } from "./aiInlineTimestampLinks";
import { remarkTimestampMentions } from "./remarkTimestampMentions";

const HighlightedCodeBody = React.lazy(
  () =>
    import(
      "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/HighlightedCodeBody"
    ),
);

function languageFromCodeClassName(className: unknown): string | undefined {
  if (typeof className !== "string") {
    return undefined;
  }
  const match = /language-([\w-]+)/.exec(className);
  return match?.[1];
}

type AgentThreadResponseMarkdownProps = {
  content: string;
  className?: string;
  datasetLinkRenderer?: DatasetLinkRenderer;
  fileLinkRenderer?: FileLinkRenderer;
  topicLinkRenderer?: TopicLinkRenderer;
  messagePathLinkRenderer?: MessagePathLinkRenderer;
  onLinkClick?: () => void;
};

/**
 * Markdown renderer for AI thread responses. Owns its own ReactMarkdown
 * configuration so thread-specific concerns (multi-language code fences,
 * AI-emitted inline timestamps, domain links) can evolve independently of
 * the shared {@link RobotoMarkdown} component used by Comments, dataset
 * descriptions, etc.
 */
export const AgentThreadResponseMarkdown: React.FC<
  AgentThreadResponseMarkdownProps
> = ({
  content,
  className,
  datasetLinkRenderer,
  fileLinkRenderer,
  topicLinkRenderer,
  messagePathLinkRenderer,
  onLinkClick,
}: AgentThreadResponseMarkdownProps) => {
  const remarkPlugins: PluggableList = [
    remarkEmoji,
    ...(browserSupportsNamedGroups() ? [remarkGfm] : []),
    remarkTimestampMentions,
  ];

  const rehypePlugins: PluggableList = [
    [rehypeExternalLinks, { target: "_blank" }],
  ];

  const containerClassName = classNames(
    styles.markdownVariantCompact,
    className,
  );

  const components: Components = {
    a: ({ href, children }) => (
      <AgentThreadMarkdownLink
        href={href}
        datasetLinkRenderer={datasetLinkRenderer}
        fileLinkRenderer={fileLinkRenderer}
        topicLinkRenderer={topicLinkRenderer}
        messagePathLinkRenderer={messagePathLinkRenderer}
        onLinkClick={onLinkClick}
      >
        {children}
      </AgentThreadMarkdownLink>
    ),
    pre: ({ children }) => {
      const codeElement = React.Children.toArray(children).find(
        (child): child is React.ReactElement =>
          React.isValidElement(child) && child.type === "code",
      );
      if (!codeElement) {
        return <pre>{children}</pre>;
      }
      const codeProps = codeElement.props as {
        className?: string;
        children?: React.ReactNode;
      };
      const language = languageFromCodeClassName(codeProps.className);
      const code =
        typeof codeProps.children === "string"
          ? codeProps.children.replace(/\n$/, "")
          : "";
      return (
        <CodeBlock
          code={code}
          language={language}
          highlightedChildren={
            language ? (
              <React.Suspense fallback={<code>{code}</code>}>
                <HighlightedCodeBody code={code} language={language} />
              </React.Suspense>
            ) : undefined
          }
        />
      );
    },
  };

  const onCopy = useMarkdownClipboardCopy();

  return (
    <Box data-cy="markdown-container" onCopy={onCopy}>
      <ReactMarkdown
        rehypePlugins={rehypePlugins}
        remarkPlugins={remarkPlugins}
        className={containerClassName}
        urlTransform={(url) => {
          if (
            isTimestampLink(url) ||
            isAIInlineTimestampLink(url) ||
            isDatasetLink(url) ||
            isFileLink(url) ||
            isTopicLink(url) ||
            isMessagePathLink(url) ||
            isRobotoUri(url)
          ) {
            return url;
          }

          return defaultUrlTransform(url);
        }}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};
